// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import "@cloudscape-design/global-styles/index.css";

import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import {
	Box,
	ColumnLayout,
	Container,
	ContentLayout,
	Header,
	SpaceBetween,
	Spinner,
	Table,
} from "@cloudscape-design/components";

import { UsageRecord } from "../../types/usageCostAllocation";
import { formatTimestamp } from "../../util/formatTimestamp";
import { useUsageRecords } from "./hooks/useUsageRecords";
import BillingPeriodSelector from "./billingPeriodSelector";

const features = require("../../features.json");

function getCurrentBillingPeriod(): string {
	const now = new Date();
	return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function formatCurrency(value: number): string {
	return new Intl.NumberFormat(undefined, {
		style: "currency",
		currency: "USD",
	}).format(value);
}

function formatCostRate(value: number): string {
	if (value === 0) {
		return formatCurrency(0);
	}
	if (value < 0.01) {
		return new Intl.NumberFormat(undefined, {
			style: "currency",
			currency: "USD",
			minimumSignificantDigits: 2,
			maximumSignificantDigits: 6,
		}).format(value);
	}
	return formatCurrency(value);
}

export default function UserUsageDashboard() {
	const { t } = useTranslation();
	const [billingPeriod, setBillingPeriod] = useState(getCurrentBillingPeriod());
	const { records, summary, loading } = useUsageRecords(billingPeriod);

	return (
		<ContentLayout
			header={
				<Header variant="h1" description={t("usage_dashboard_description")}>
					{t("usage_dashboard_title")}
				</Header>
			}
		>
			<SpaceBetween size="l">
				<BillingPeriodSelector
					selectedPeriod={billingPeriod}
					onChange={setBillingPeriod}
				/>

				{loading ? (
					<Box textAlign="center" margin={{ vertical: "l" }}>
						<Spinner size="large" />
					</Box>
				) : (
					<>
						<Container header={<Header variant="h2">{t("usage_summary")}</Header>}>
							<ColumnLayout columns={features.costingEnabled ? 2 : 1} variant="text-grid">
								<div>
									<Box variant="awsui-key-label">{t("usage_total_billable_units")}</Box>
									<Box variant="awsui-value-large">{summary.totalCharacterCount}</Box>
								</div>
								{features.costingEnabled && (
									<div>
										<Box variant="awsui-key-label">{t("usage_total_estimated_cost")}</Box>
										<Box variant="awsui-value-large">{formatCurrency(summary.totalEstimatedCost)}</Box>
									</div>
								)}
							</ColumnLayout>
						</Container>

						<Table
							columnDefinitions={[
								{
									id: "metricValue",
									header: t("usage_col_kb"),
									cell: (item: UsageRecord) => item.metricValue,
								},
								...(features.costingEnabled
									? [
										{
											id: "costRateApplied",
											header: t("usage_col_cost_rate"),
											cell: (item: UsageRecord) => formatCostRate(item.costRateApplied),
										},
										{
											id: "estimatedCost",
											header: t("usage_col_estimated_cost"),
											cell: (item: UsageRecord) => formatCurrency(item.estimatedCost),
										},
									]
									: []),
								{
									id: "timestamp",
									header: t("usage_col_timestamp"),
									cell: (item: UsageRecord) => formatTimestamp(item.timestamp),
								},
							]}
							items={records}
							trackBy="id"
							loading={loading}
							loadingText={t("generic_loading")}
							stripedRows
							stickyHeader
							header={
								<Header counter={`(${records.length})`}>
									{t("usage_records_title")}
								</Header>
							}
							empty={
								<Box textAlign="center" color="inherit" margin={{ vertical: "xs" }}>
									<b>{t("usage_no_records")}</b>
								</Box>
							}
						/>
					</>
				)}
			</SpaceBetween>
		</ContentLayout>
	);
}
